
import { AnimatePresence, motion } from "framer-motion";
import { X, Heart, Search, Trash2, ExternalLink, RotateCcw } from "lucide-react";
import {
  JL_ORIGIN,
  formatPrice,
  titleCase,
  jackLingoUrl,
} from "../../lib/jlApi";
import type { Favorite } from "../../hooks/useFavorites";
import type { SavedSearch } from "../../hooks/useSavedSearches";

interface SavedDrawerProps {
  open: boolean;
  onClose: () => void;
  favorites: Favorite[];
  onRemoveFavorite: (listingId: string) => void;
  searches: SavedSearch[];
  onRestoreSearch: (params: string) => void;
  onRemoveSearch: (id: string) => void;
}

function daysAgo(ts: number): number {
  return Math.floor((Date.now() - ts) / (1000 * 60 * 60 * 24));
}

function savedLabel(ts: number): string {
  const d = daysAgo(ts);
  if (d <= 0) return "saved today";
  if (d === 1) return "saved yesterday";
  return `saved ${d} days ago`;
}

export default function SavedDrawer({
  open,
  onClose,
  favorites,
  onRemoveFavorite,
  searches,
  onRestoreSearch,
  onRemoveSearch,
}: SavedDrawerProps) {
  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-[19500] bg-black/40"
          />
          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", stiffness: 320, damping: 34 }}
            className="fixed right-0 top-0 z-[19600] flex h-full w-full max-w-md flex-col bg-[var(--cream)] shadow-2xl"
            role="dialog"
            aria-modal="true"
            aria-label="Saved homes and searches"
          >
            <div className="flex items-center justify-between border-b border-[var(--sand)] bg-white px-4 py-3">
              <span
                className="text-lg font-semibold text-[var(--navy)]"
                style={{ fontFamily: "var(--font-playfair)" }}
              >
                Saved
              </span>
              <button
                onClick={onClose}
                aria-label="Close saved panel"
                className="p-1 text-[var(--slate)] active:text-[var(--navy)]"
              >
                <X size={20} />
              </button>
            </div>

            <div className="flex-1 space-y-6 overflow-y-auto p-4 pb-[calc(1rem+env(safe-area-inset-bottom))]">
              {/* Favorites */}
              <section>
                <h3 className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-[var(--navy)]">
                  <Heart size={15} className="text-[var(--gold)]" /> Saved homes
                  <span className="text-[var(--slate)]">({favorites.length})</span>
                </h3>
                {favorites.length === 0 ? (
                  <p className="text-sm text-[var(--slate)]">
                    Tap the heart on any home to save it here.
                  </p>
                ) : (
                  <ul className="space-y-2">
                    {favorites.map(({ listing, savedAt }) => {
                      const stale = daysAgo(savedAt) >= 7;
                      return (
                        <li
                          key={listing.listingId}
                          className="flex gap-3 rounded-xl border border-[var(--sand)] bg-white p-2"
                        >
                          {listing.imageUrl ? (
                            <img
                              src={`${JL_ORIGIN}${listing.imageUrl}`}
                              alt=""
                              className="h-16 w-20 flex-shrink-0 rounded-lg object-cover"
                            />
                          ) : (
                            <div className="h-16 w-20 flex-shrink-0 rounded-lg bg-[var(--sand)]" />
                          )}
                          <div className="min-w-0 flex-1">
                            <p className="font-semibold text-[var(--navy)]">
                              {listing.priceDisplay || formatPrice(listing.price)}
                            </p>
                            <p className="truncate text-sm text-[var(--slate)]">
                              {titleCase(listing.street)}, {titleCase(listing.city)}
                            </p>
                            <p className="text-xs text-[var(--slate)]">
                              {savedLabel(savedAt)}
                              {stale && " \u2014 verify current status"}
                            </p>
                            <div className="mt-1 flex items-center gap-3">
                              <a
                                href={jackLingoUrl(listing.listingId)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-xs font-medium text-[var(--navy)] hover:text-[var(--gold)]"
                              >
                                <ExternalLink size={12} /> View
                              </a>
                              <button
                                onClick={() => onRemoveFavorite(listing.listingId)}
                                className="inline-flex items-center gap-1 text-xs text-[var(--slate)] hover:text-red-600"
                              >
                                <Trash2 size={12} /> Remove
                              </button>
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </section>

              {/* Saved searches */}
              <section>
                <h3 className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-[var(--navy)]">
                  <Search size={15} className="text-[var(--gold)]" /> Saved searches
                  <span className="text-[var(--slate)]">({searches.length})</span>
                </h3>
                {searches.length === 0 ? (
                  <p className="text-sm text-[var(--slate)]">
                    Use &ldquo;Save this search&rdquo; to store your filters + map view.
                  </p>
                ) : (
                  <ul className="space-y-2">
                    {searches.map((s) => (
                      <li
                        key={s.id}
                        className="flex items-center gap-2 rounded-xl border border-[var(--sand)] bg-white p-3"
                      >
                        <div className="min-w-0 flex-1">
                          <p className="truncate font-medium text-[var(--navy)]">
                            {s.name}
                          </p>
                          <p className="text-xs text-[var(--slate)]">
                            {savedLabel(s.savedAt)}
                          </p>
                        </div>
                        <button
                          onClick={() => onRestoreSearch(s.params)}
                          className="inline-flex items-center gap-1 rounded-lg bg-[var(--navy)] px-3 py-1.5 text-xs font-medium text-white hover:bg-[var(--gold)]"
                        >
                          <RotateCcw size={12} /> Restore
                        </button>
                        <button
                          onClick={() => onRemoveSearch(s.id)}
                          aria-label="Delete saved search"
                          className="p-1 text-[var(--slate)] hover:text-red-600"
                        >
                          <Trash2 size={14} />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
